import { Button, Typography } from '@material-ui/core';
import { AxiosError } from 'axios';
import { AssembleiaIcon, ChartIcon, SyncIcon } from 'components/Icons';
import ResultadosModal, { ResultadosModalProps } from 'components/ResultadosModal';
import { ApiErrors } from 'enuns/ApiErrors';
import { AssembleiaPautaStatus } from 'enuns/AssembleiaPautaStatus';
import { AssembleiaStatus } from 'enuns/AssembleiaStatus';
import React, { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { assembleiaAssociadoActionsRefresh, assembleiasAssociadoActionsRegistraVoto } from 'store/modules/AssembleiasAssociadoActions';
import { modalsActionsOpen } from 'store/modules/ModalsActions';
import { IReducer } from 'store/Reducer';
import { getContasSelecionadasPeloUsuario, getPermitirVoto } from 'utils/AssembleiaUtils';
import AxiosErrorHandler, { getAxiosErrorApiCode } from 'utils/AxiosErrorHandler';
import { getUniqueKey } from 'utils/KeyUtils';
import { getToastSuccess } from 'utils/ToasterUtils';
import { PautaVotoInvalido } from './AssembleiaPauta';
import { IPauta } from './AssembleiaPautas';
import useStyles from './style';

export interface IAssembleiaPautaAssociadoActionButtons {
  totalContas?: number;
  totalPresentes?: number;
  loading: boolean,
  setLoading: Function,
  pauta: IPauta,
  voto: number,
  onVotoError: Function
}

export default ({
  totalContas,
  totalPresentes,
  loading,
  setLoading,
  pauta,
  voto,
  onVotoError
}: IAssembleiaPautaAssociadoActionButtons) => {
  const { t } = useTranslation();
  const [sending, setSending] = useState<boolean>(false);
  const dispatchAction = useDispatch();
  const { totalConvocacoes } = useSelector((state: IReducer) => state.assembleias);
  const currentEvent = useSelector((state: IReducer) => state.assembleias.currentEvent);
  const usuario = useSelector((state: IReducer) => state.auth.usuario);
  const classes = useStyles();

  const getVotoAtual = useCallback((): number | null => {
    if (pauta.votos && pauta.votos.length) {
      return pauta.votos[0].idItemPauta;
    }
    return PautaVotoInvalido;
  }, [pauta.votos]);

  const confirmarVotoHidden = useCallback((): boolean => {
    return !getPermitirVoto(currentEvent?.contasDisponiveis || [], usuario!.cpfCnpj);
  }, [currentEvent, usuario])

const isDisabled = useCallback(() => {
  if (!usuario!.perfil.associadoRoute.assembleias.pautas.participar) {
    return true;
  }
  if (getContasSelecionadasPeloUsuario(currentEvent && currentEvent.contasDisponiveis ? currentEvent.contasDisponiveis : [], usuario!.cpfCnpj).length === 0) {
    return true;
  }
  if (
    !getPermitirVoto(currentEvent && currentEvent.contasDisponiveis ?
      currentEvent.contasDisponiveis : [], usuario!.cpfCnpj) ||
    sending ||
    loading ||
    (!pauta.itens || pauta.itens.length === 0) ||
    !voto ||
    voto === PautaVotoInvalido ||
    pauta.status !== AssembleiaPautaStatus.Liberada ||
    voto === getVotoAtual()
  ) {
    return true;
  }
  return false;
}, [pauta, currentEvent, usuario, sending, voto, loading, getVotoAtual]);
// }, [pauta, currentEvent, usuario, sending, loading]);

const registarVoto = useCallback(() => {
  setSending(true);
}, [setSending])

  const showError = useCallback((error: AxiosError) => {
    setSending(false);
    AxiosErrorHandler(error);
    onVotoError();
    if (getAxiosErrorApiCode(error) === ApiErrors.PautaNaoLiberadaParaVotacao && currentEvent) {
      dispatchAction(
        assembleiaAssociadoActionsRefresh({
          id: currentEvent.idAssembleia,
          isChatAtivo: currentEvent.statusChat,
          pautas: [
            {
              flgResultadoLiberado: pauta.flgResultadoLiberado,
              idPauta: pauta.idPauta,
              status: AssembleiaPautaStatus.Encerrada,
              tempoVotacao: 0,
              tempoRestante: 0
            }
          ],
          usuariosLogados: currentEvent.usuariosLogados,
          presentes: currentEvent.presentes,
          status: currentEvent.status,
          messenger: null,
        })
      )
    }
  }, [onVotoError, setSending, dispatchAction, currentEvent, pauta])

  const mostrarBloqueioLabel = useMemo(() => {
    return !!(
      currentEvent &&
      currentEvent?.status === AssembleiaStatus.Live &&
      !!totalPresentes && totalPresentes > 0 && 
      confirmarVotoHidden()
    )
  }, [totalPresentes, currentEvent, confirmarVotoHidden])

  const bloqueioLabel = useMemo(() => {
    return mostrarBloqueioLabel && (
      <Typography key={0} component="p" variant="caption" color="textPrimary" className={classes.pautaFooterLabel}>
        {t('AssembleiaPauta.acoes.bloqueioColaborador')}
      </Typography>
    )
  }, [mostrarBloqueioLabel, t, classes])
  
  const mostrarRegistrarVotoButton = useMemo(() => {
    return (
      currentEvent &&
      currentEvent?.status === AssembleiaStatus.Live &&
      pauta.status === AssembleiaPautaStatus.Liberada &&
      !!totalPresentes && totalPresentes > 0 && 
      !isDisabled() &&
      !confirmarVotoHidden()
    )
  }, [totalPresentes, currentEvent, pauta, isDisabled, confirmarVotoHidden])

  const registrarVotoButton = useMemo(() => {
    return mostrarRegistrarVotoButton && (
      <Button
        key={1}
        className={ classes.botaoAcoes }
        variant="contained"
        color="primary"
        onClick={ registarVoto }
        startIcon={ <AssembleiaIcon /> }
        endIcon={ sending ? <SyncIcon className="animation-rotate" /> : null }
      >
        { t('AssembleiaPauta.acoes.confirmar') }
      </Button>
    )
  }, [mostrarRegistrarVotoButton, classes, sending, registarVoto, t])

  const mostrarResultadoButton = useMemo(() => {
    return !!(
      (
        (currentEvent && currentEvent.status === AssembleiaStatus.Live) &&
        pauta.status === AssembleiaPautaStatus.Encerrada &&
        pauta.flgResultadoLiberado &&
        !!totalContas && totalContas > 0
      ) || (
        (currentEvent && currentEvent.status === AssembleiaStatus.Encerrado) &&
        pauta.status === AssembleiaPautaStatus.Encerrada &&
        pauta.flgResultadoLiberado &&
        totalConvocacoes > 0
      )
  )}, [currentEvent, pauta, totalContas, totalConvocacoes])

  const openResultadosModal = useCallback(() => {
    const modalKey: string = getUniqueKey();
    dispatchAction(
      modalsActionsOpen(
        ResultadosModal,
        {
          genericProps: {
            key: modalKey,
            idPauta: pauta.idPauta,
            flgAssessor: false
          } as ResultadosModalProps,
        },
        modalKey
      )
    );
  }, [dispatchAction, pauta]);

  const resultadoButton = useMemo(() => {
    return mostrarResultadoButton && (
      <Button
        key={2}
        className={ classes.botaoAcoes }
        variant="contained"
        color="primary"
        startIcon={ <ChartIcon /> }
        onClick={openResultadosModal}
      >
        { t('AssembleiaPauta.acoes.resultado') }
      </Button>
    )
  }, [mostrarResultadoButton, t, classes, openResultadosModal])

  const buttons = useMemo<ReactNode[]>(() => {
    return [
      registrarVotoButton,
      resultadoButton,
    ]
  }, [
    registrarVotoButton,
    resultadoButton,
  ])

  useEffect(() => {
    setLoading(sending)
  }, [sending, setLoading]);

  useEffect(() => {
    if (sending && !loading && currentEvent) {
      dispatchAction(assembleiasAssociadoActionsRegistraVoto(
        voto,
        () => {
          getToastSuccess(t('AssembleiaPauta.acoes.confirmado'))
          setSending(false);
        },
        showError
      ))
    }
  }, [usuario, t, voto, sending, currentEvent, loading, dispatchAction, showError]);

  return !!bloqueioLabel || buttons.filter(it => it !== null) ? (
    <div className={classes.pautaFooter}>
      {
        !!bloqueioLabel && (
          <div>
            {bloqueioLabel}
          </div>
        )
      }
      {
        buttons.length > 0 && (
          <div>
            {buttons.map(it => it)}
          </div>
        )
      }
    </div>
  ) : null;
}